/**
 * Every number in Dimples lives here.
 *
 * The design plan's one structural promise: this is the only file you touch
 * during a tuning pass. Nothing below is sacred — they are first guesses meant
 * to be beaten by an afternoon of play.
 */

import type { AngerTier, GolferSpec, HoleSpec, LieSpec } from './types'

// ── Clocks ───────────────────────────────────────────────────────────────────

/** Fixed simulation rate. At top ball speed one step is under a metre. */
export const SIM_HZ = 120
export const SIM_DT = 1 / SIM_HZ
/** Tab-switch protection: never simulate more than this much at once. */
export const MAX_FRAME = 0.25

export const GRAVITY = 9.81

// ── Tee geometry: the four seconds that matter ───────────────────────────────
//
// The ball rests below the clubface sweet spot, so doing nothing is a bladed
// shot — legal, legible, and never better than playing. Holding squats you
// clear of the club entirely (the dodge). Releasing hops you up toward the
// sweet spot: the apex is the forgiveness window, which is exactly why an
// analog hold beats a frame-perfect tap on a phone.

/** Cartoon scale — a real ball is 21mm, this reads at arm's length. */
export const BALL_R = 0.035
/** A low rubber range tee. */
export const TEE_TOP_Y = 0.06
export const BALL_REST_Y = TEE_TOP_Y + BALL_R

/** How far above the resting ball the clubface sweet spot passes. */
export const SWEET_RISE = 0.085
export const SWEET_Y = BALL_REST_Y + SWEET_RISE

/** Full crouch depth, and how long a hold takes to reach it. At full crouch the
 *  ball is a pancake on the mat and the club sails clean over the top. */
export const SQUAT_DEPTH = 0.09
export const SQUAT_TIME = 0.4
/** Crouching back up when you let go without committing. */
export const UNSQUAT_TIME = 0.22

/**
 * Hop launch speed as a function of charge. The flush charge works out around
 * 0.45, so both under- and over-holding are real errors.
 * v0 needed to rise SWEET_RISE = sqrt(2*g*0.085) = 1.291 m/s
 */
export const HOP_V_MIN = 0.7
export const HOP_V_MAX = 2.1

/** Contact thresholds on |ball centre − sweet spot|, in metres. */
export const TIER_FLUSH = 0.025
export const TIER_GOOD = 0.055
export const TIER_THIN = 0.095
export const TIER_SHANK = 0.14
// beyond TIER_SHANK the club misses entirely: a whiff, which is the dodge.

/** Launch speed multiplier per tier. */
export const SMASH = { FLUSH: 1.0, GOOD: 0.88, THIN: 0.65, SHANK: 0.4, WHIFF: 0 } as const
/** Ball damage per tier. */
export const TIER_DAMAGE = { FLUSH: 0, GOOD: 0, THIN: 5, SHANK: 10, WHIFF: 0 } as const

/** Launch angle by tier. Struck low = screamer, struck high = sky ball. */
export const LAUNCH_DEG = {
	FLUSH: 13.5,
	GOOD: 14.5,
	THIN_LOW: 5,
	THIN_HIGH: 34,
	SHANK: 22,
} as const
export const SPIN_BACK = {
	FLUSH: 1.05,
	GOOD: 0.95,
	THIN_LOW: 0.35,
	THIN_HIGH: 1.9,
	SHANK: 0.6,
} as const
/** Extra offline degrees the strike itself contributes. */
export const OFFLINE_DEG = { FLUSH: 0, GOOD: 3, THIN: 8, SHANK: 40 } as const

// ── Lies: where the ball is sitting, and what it can do about it ─────────────
//
// The hole is played out, so a shot finishing badly is a punishment you then
// have to hit your way out of. The lie enforces that by moving two numbers
// against each other: `sweetRise` (how far above you the clubface passes) and
// `hopMult` (how high you can jump). In the rough the grass holds you down
// until the sweet spot is out of reach, so a perfect release still only earns
// a GOOD. In sand it is out of reach by a mile and THIN is the ceiling.
//
// Max hop apex above rest = (HOP_V_MAX * hopMult)^2 / 2g. Compare that with
// sweetRise to read a lie's ceiling straight off the table.

export const LIES: Record<string, LieSpec> = {
	// A peg. The only lie where the ball is held up to meet the club.
	tee: {
		name: 'tee',
		restY: 0.095,
		sweetRise: 0.085,
		hopMult: 1,
		speedMult: 1,
		offlineDeg: 0,
		launchDeg: 0,
		mud: 0,
	},
	// Clean turf: apex 0.162 against a 0.075 rise, so FLUSH is comfortably there.
	fairway: {
		name: 'fairway',
		restY: 0.035,
		sweetRise: 0.075,
		hopMult: 0.85,
		speedMult: 0.95,
		offlineDeg: 0,
		launchDeg: 2,
		mud: 0,
	},
	// Apex 0.0275 against a 0.075 rise: you cannot reach it. Ceiling is GOOD.
	rough: {
		name: 'rough',
		restY: 0.02,
		sweetRise: 0.075,
		hopMult: 0.35,
		speedMult: 0.72,
		offlineDeg: 6,
		launchDeg: 9,
		mud: 0.05,
	},
	// Apex 0.005 against 0.08. Ceiling is THIN, and it goes almost straight up.
	bunker: {
		name: 'sand',
		restY: 0.012,
		sweetRise: 0.08,
		hopMult: 0.15,
		speedMult: 0.55,
		offlineDeg: 9,
		launchDeg: 24,
		mud: 0,
	},
	// Concrete gives back. You can jump higher off it than off a tee — and it
	// has no idea where you were aiming.
	cartpath: {
		name: 'cart path',
		restY: 0.035,
		sweetRise: 0.085,
		hopMult: 1.3,
		speedMult: 1.06,
		offlineDeg: 15,
		launchDeg: -3,
		mud: 0,
	},
	// A putt. The putter transfers power perfectly well — it is the *club
	// selection* that makes a six metre putt a six metre putt, so crushing the
	// speed here too was double-counting and left putts that never moved at all.
	// What the green really changes is that the ball rolls instead of flying.
	green: {
		name: 'green',
		restY: 0.035,
		sweetRise: 0.03,
		hopMult: 0.25,
		speedMult: 0.85,
		offlineDeg: 0,
		launchDeg: -12,
		mud: 0,
		maxLaunchDeg: 3,
		spinMult: 0.04,
	},
	ob: {
		name: 'deep stuff',
		restY: 0.018,
		sweetRise: 0.08,
		hopMult: 0.28,
		speedMult: 0.62,
		offlineDeg: 10,
		launchDeg: 12,
		mud: 0.08,
	},
	tee_mat: {
		name: 'mat',
		restY: 0.095,
		sweetRise: 0.085,
		hopMult: 1,
		speedMult: 1,
		offlineDeg: 0,
		launchDeg: 0,
		mud: 0,
	},
}

// ── The course ───────────────────────────────────────────────────────────────
//
// Six holes, played as a round. Length is the main lever — it decides which
// golfer can reach and therefore who is worth waiting for in the bucket — and
// after that each hole has one idea: a pond you must carry, a green tucked
// behind sand, a corridor of trees, a green set off the line so the hole bends.
//
// Par is set from length the way a real card does it: under 230m is a three,
// under 430m a four, beyond that a five.

const par = (d: number) => (d < 230 ? 3 : d < 430 ? 4 : 5)

function hole(o: {
	no: number
	name: string
	len: number
	/** Lateral offset of the green. Non-zero bends the hole. */
	offset?: number
	water?: HoleSpec['water']
	bunkers?: HoleSpec['bunkers']
	cartpath?: HoleSpec['cartpath']
	trees?: HoleSpec['trees']
	wind?: HoleSpec['wind']
	greenR?: number
	fairwayHalfW?: number
}): HoleSpec {
	const gx = o.offset ?? 0
	// The cup sits toward the back of the green, where a roll-out dies.
	const gz = o.len - 5
	return {
		no: o.no,
		name: o.name,
		par: par(o.len),
		tee: { x: 0, z: 0 },
		pin: { x: gx, z: o.len },
		green: { x: gx, z: gz, r: o.greenR ?? 16 },
		fairwayHalfW: o.fairwayHalfW ?? 14,
		roughHalfW: (o.fairwayHalfW ?? 14) + 12,
		water: o.water ?? [],
		bunkers: o.bunkers ?? [],
		cartpath: o.cartpath ?? null,
		trees: o.trees ?? [],
		wind: o.wind ?? { speed: 0, dirDeg: 0 },
	}
}

/** A line of trees down one side, cheap to declare and nasty to be behind. */
function treeLine(x: number, z0: number, z1: number, n: number, r = 1.5, h = 9) {
	return Array.from({ length: n }, (_, i) => ({
		x,
		z: z0 + ((z1 - z0) * i) / Math.max(1, n - 1),
		r,
		h,
	}))
}

export const COURSE: HoleSpec[] = [
	hole({
		no: 1,
		name: 'The Mat',
		len: 215,
		bunkers: [{ x: 12, z: 196, r: 7 }],
		cartpath: { x0: 15.5, x1: 19.5, z0: 55, z1: 190 },
		wind: { speed: 0, dirDeg: 0 },
	}),
	hole({
		no: 2,
		name: 'Short Work',
		len: 138,
		greenR: 13,
		bunkers: [
			{ x: -9, z: 124, r: 6 },
			{ x: 10, z: 128, r: 6 },
		],
		trees: treeLine(-19, 40, 110, 5),
		wind: { speed: 3.5, dirDeg: 90 },
	}),
	hole({
		no: 3,
		name: 'The Carry',
		len: 268,
		water: [{ x0: -16, x1: 16, z0: 150, z1: 205 }],
		bunkers: [{ x: -13, z: 250, r: 6 }],
		wind: { speed: 2, dirDeg: 180 },
	}),
	hole({
		no: 4,
		name: 'Bendy',
		len: 300,
		offset: 26,
		fairwayHalfW: 17,
		trees: [...treeLine(-14, 90, 200, 6), ...treeLine(30, 60, 150, 4)],
		bunkers: [{ x: 14, z: 268, r: 7 }],
		wind: { speed: 4, dirDeg: 250 },
	}),
	hole({
		no: 5,
		name: 'The Avenue',
		len: 355,
		fairwayHalfW: 12,
		trees: [...treeLine(-15, 70, 300, 9), ...treeLine(15, 70, 300, 9)],
		cartpath: { x0: -22, x1: -18, z0: 40, z1: 330 },
		wind: { speed: 1.5, dirDeg: 20 },
	}),
	hole({
		no: 6,
		name: 'Long Way Home',
		len: 445,
		offset: -18,
		water: [{ x0: 8, x1: 30, z0: 300, z1: 380 }],
		bunkers: [
			{ x: -30, z: 415, r: 8 },
			{ x: -4, z: 300, r: 6 },
		],
		trees: treeLine(22, 120, 260, 5),
		wind: { speed: 5, dirDeg: 300 },
	}),
]

/** How hard a tree deflects a ball that clips it, and what it costs. */
export const TREE_DAMPING = 0.28
export const TREE_DAMAGE = 8
/** Wind acts on the ball's airspeed. Real, but gentler than reality. */
export const WIND_K = 0.55

// ── The hole ─────────────────────────────────────────────────────────────────
//
// You play it out. The golfer who took you off the tee walks up and hits you
// again from wherever you stopped, carrying their anger with them — so ducking
// mid-hole now costs you your ride to the green.

/** A 215m par 3. Holing in one is the ace; the rest of us take three. */
export const HOLE_PAR = 3
/** Past this the golfer gives up on you and pockets the ball. */
export const MAX_STROKES = 8
/** Strokes added for a ball that has to be dropped rather than played. */
export const PENALTY_STROKE = 1
/** Seconds the golfer spends walking up to your ball before the next swing. */
export const WALK_TIME = 1.1
/**
 * The look down the line before every stroke: long enough to read the hole, the
 * wind and the distance, and to get your thumb ready. Without it the swing
 * arrives while the player is still working out where they are.
 */
export const AIM_TIME = 2.4
/** Score for finishing a hole, by strokes taken relative to par. */
export const HOLE_SCORE: Record<number, number> = {
	1: 6000,
	2: 2200,
	3: 1000,
	4: 450,
	5: 200,
}
export const HOLE_SCORE_MIN = 80

/**
 * Rolling away off the tee, which is the only thing the ball does that a golfer
 * could not possibly mistake for his own bad swing. Held during the address
 * only, so it can never collide with the crouch during a live swing.
 */
export const ROLL_AWAY_HOLD = 0.85
export const ROLL_AWAY_SUN = 1

// ── Swing timeline ───────────────────────────────────────────────────────────

export const ADDRESS_MIN = 1.2
export const ADDRESS_MAX = 2.5
/** After the first swing against a golfer, re-addressing is snappier: the
 *  tantrum plays during the reset, so the comedy beat *is* the pacing fix. */
export const ADDRESS_REPEAT = 1.35
export const BACKSWING_TIME = 0.7
export const TOP_PAUSE = 0.4
export const FOLLOW_TIME = 0.9

/**
 * Swing arc: the club is a rigid rod rotating about a shoulder pivot behind and
 * above the ball. CLUB_LEN and SWING_ANGLE_IMPACT are *derived*, not chosen, so
 * that the clubface sweet spot passes exactly through (0, SWEET_Y, 0) — the
 * simulation is authoritative about where the club is and the art follows it.
 * Pick the pivot; the rest falls out.
 */
export const PIVOT = { x: 0, y: 1.28, z: -0.52 }
export const CLUB_LEN = Math.hypot(PIVOT.y - SWEET_Y, PIVOT.z)
/**
 * Angles follow the scene-graph convention exactly: the club hangs along local
 * −Y and the assembly is rotated about +X, so `Rx(a)·(0,−L,0)` puts the head at
 * `(0, PIVOT.y − L·cos a, PIVOT.z − L·sin a)`. Getting this sign wrong mirrors
 * the club path between the simulation and the renderer.
 *
 * The swing sweeps monotonically from TOP down through IMPACT to FOLLOW.
 */
export const SWING_ANGLE_IMPACT = Math.atan2(PIVOT.z, PIVOT.y - SWEET_Y)
export const SWING_ANGLE_ADDRESS = 0.06
export const SWING_ANGLE_TOP = 2.75
export const SWING_ANGLE_FOLLOW = -2.5

/**
 * Cartoon driver head. The half-height plus the ball radius equals TIER_SHANK
 * exactly, so "the club geometrically missed" and "the tier table says WHIFF"
 * are the same statement — a full crouch visibly sails under the head.
 */
export const CLUB_FACE_H = 2 * (TIER_SHANK - BALL_R)
export const CLUB_FACE_W = 0.22

/**
 * The phone. Rarely, a golfer's phone goes off at the top of the backswing.
 *
 * The swing that follows is hopeless and ducking it costs no anger at all — the
 * only free duck in the game, and framed as entirely their fault rather than a
 * gift. It exists because a push-your-luck loop needs one moment where the
 * pressure lifts and the joke is on somebody else.
 */
export const PHONE_CHANCE = 0.08
/** How badly the phone ruins the strike, as a multiplier on launch speed. */
export const PHONE_SMASH = 0.55
/** And how much wider it throws the aim, in degrees of extra sigma. */
export const PHONE_DISPERSION = 14

// ── The audio telegraph ──────────────────────────────────────────────────────
/** Ticks before impact, in *simulation* seconds. Players learn it as a rhythm,
 *  which is what keeps a compressed angry swing fair. */
export const TICKS = [0.4, 0.2, 0.0]

// ── Slow motion: the ramp IS the input window ────────────────────────────────
export const RAMP_TIME = 0.35
export const RAMP_TIMESCALE = 0.3
/** Anger shortens the ramp and raises its floor: reading time and difficulty
 *  are deliberately the same knob. */
export const RAMP_ANGER_SHORTEN = 0.15
export const RAMP_FLOOR_AT_MAX_ANGER = 0.45

/** Hitstop duration by tier, in unscaled seconds. */
export const HITSTOP = { FLUSH: 0.14, GOOD: 0.09, THIN: 0.045, SHANK: 0.03, WHIFF: 0 } as const
/** Trauma added to the camera shake by tier. Shank shakes *more*, and wrong. */
export const TRAUMA = { FLUSH: 0.5, GOOD: 0.4, THIN: 0.35, SHANK: 0.6, WHIFF: 0.12 } as const
/** After the cut to the flight camera, time recovers over this long. */
export const LAUNCH_RECOVER = 0.5

// ── Anger ────────────────────────────────────────────────────────────────────
//
// Storm-off is a *roll*, not a fuse. With a deterministic fuse the expected
// score rose monotonically with every dodge, so max-dodging was simply correct
// and the gamble was fake. Under these odds the payoff curve peaks near two
// dodges, and a third is a genuinely held breath.

export const ANGER: AngerTier[] = [
	{ name: 'Calm', power: 1.0, dispersionDeg: 2, downswing: 0.6, stormOff: 0.0 },
	{ name: 'Miffed', power: 1.1, dispersionDeg: 5, downswing: 0.54, stormOff: 0.0 },
	{ name: 'Steamed', power: 1.22, dispersionDeg: 10, downswing: 0.48, stormOff: 0.2 },
	{ name: 'Furious', power: 1.34, dispersionDeg: 18, downswing: 0.42, stormOff: 0.4 },
	{ name: 'Volcanic', power: 1.48, dispersionDeg: 26, downswing: 0.38, stormOff: 0.65 },
]
export const MAX_ANGER = ANGER.length - 1

// ── The cast ─────────────────────────────────────────────────────────────────

export const GOLFERS: GolferSpec[] = [
	{
		id: 'gary',
		name: 'Gary',
		speedMult: 0.86,
		dispersionDeg: 8,
		sweetSpotMult: 1.0,
		temperMult: 0.7,
		tempoMult: 1.12,
		chaos: 0.05,
		color: 0xf2a0a0,
		build: 2,
	},
	{
		id: 'rick',
		name: 'Long-Drive Rick',
		speedMult: 1.19,
		dispersionDeg: 6,
		sweetSpotMult: 0.85,
		temperMult: 1.3,
		tempoMult: 0.9,
		chaos: 0.1,
		color: 0x9fd8c0,
		build: 1,
	},
	{
		id: 'dana',
		name: 'Old Pro Dana',
		speedMult: 0.98,
		dispersionDeg: 1,
		sweetSpotMult: 1.6,
		temperMult: 1.6,
		tempoMult: 1.2,
		// The metronome. She is the only golfer you can play from memory, which is
		// most of why she is the one who can ace.
		chaos: 0,
		color: 0xa8c4e8,
		build: 0,
	},
	{
		// Two-Beer Terry. Enormous, cheerful, and completely unrepeatable: his
		// downswing wanders by half its own length from one swing to the next, so
		// the rhythm you learned on Dana is worthless. The audio telegraph still
		// tells the truth, so he is unreliable rather than unfair — you just have
		// to actually listen instead of counting. Almost impossible to anger,
		// because he is having a lovely time.
		id: 'terry',
		name: 'Two-Beer Terry',
		speedMult: 1.1,
		dispersionDeg: 11,
		sweetSpotMult: 1.15,
		temperMult: 0.25,
		tempoMult: 1.25,
		chaos: 0.45,
		color: 0xe8b86a,
		build: 2,
	},
]

// ── Ballistics ───────────────────────────────────────────────────────────────

/**
 * Base launch speed before golfer, anger and smash multipliers.
 * Drag and lift are calibrated together — see `pnpm calibrate`, which fails
 * loudly if a change here pushes the baseline or the jackpot drive out of
 * its design window.
 */
export const BASE_SPEED = 60
/**
 * What a 1.0-speed golfer's flat-out shot carries and rolls, in metres. Used to
 * pick a club for the distance left, so it must track the calibrated distances
 * rather than being chosen freely.
 */
export const FULL_SHOT = 215
/** Quadratic drag, near real golf. Floatier than real only in the lift term. */
export const DRAG_K = 0.0042
/** Magnus lift, linear in speed. Stable, six lines, indistinguishable at arcade scale. */
export const LIFT_K = 0.165
export const SIDE_K = 0.1
/** Spin decay time constant, seconds. */
export const SPIN_TAU = 4.0

// ── The three airborne verbs, all on one button ──────────────────────────────
//
// Tap high = NUDGE (add distance). Keep holding = DIVE (take distance off).
// Tap as you land = SKIP (steal a bounce). Between them the flight stops being
// something you watch: nudge and dive are a two-way distance control, which is
// the thing the game lacked entirely, and skip is where the ceiling is.

/** Up-and-forward impulse. Clears a tree line, stretches a carry. */
export const NUDGE_IMPULSE = 4.5
export const NUDGE_DEG = 45
export const NUDGE_BUDGET = 2

/**
 * Hold on the way down and the ball drives itself into the ground, trading
 * carry for run. Unlimited on purpose: it is a control, not a resource, and it
 * is the only way a player can ever take distance *off* a shot.
 */
export const DIVE_ACCEL = 12
/** Holding this long turns a tap into a dive. Under it, it was a nudge. */
export const DIVE_HOLD = 0.16
/** Diving spins the ball forward, so a dived ball runs instead of biting. */
export const DIVE_SPIN_RATE = 1.6
export const DIVE_SPIN_MIN = -0.8

/**
 * Tap this close to touching down and the ball skips off the turf like a stone
 * instead of thudding into it. The window is generous next to the tee's, because
 * unlike the swing there is no three-beat telegraph to learn from.
 */
export const SKIP_WINDOW = 0.1
/** A skipped bounce leaves shallow and fast rather than high and slow. */
export const SKIP_KEEP = 0.92
export const SKIP_BOUNCE = 0.42
export const STYLE_SKIP_BOUNCE = 50

export const SURFACES: Record<
	string,
	{ restitution: number; tangentKeep: number; rollDecel: number; damage: number }
> = {
	tee: { restitution: 0.38, tangentKeep: 0.62, rollDecel: 4.5, damage: 0 },
	fairway: { restitution: 0.38, tangentKeep: 0.62, rollDecel: 4.5, damage: 0 },
	// A green should putt. At 2.0 the ball went from "slow enough to drop" to
	// "stopped" inside a single metre, so the window in which a hole-in-one was
	// even possible was a metre long — which is why it never happened once in
	// 27,000 simulated shots. Real greens shed about 0.5 m/s^2.
	green: { restitution: 0.3, tangentKeep: 0.55, rollDecel: 0.6, damage: 0 },
	rough: { restitution: 0.12, tangentKeep: 0.3, rollDecel: 9, damage: 0 },
	cartpath: { restitution: 0.8, tangentKeep: 0.9, rollDecel: 1.2, damage: 12 },
	bunker: { restitution: 0.05, tangentKeep: 0.15, rollDecel: 12, damage: 3 },
	water: { restitution: 0, tangentKeep: 0, rollDecel: 0, damage: 0 },
	ob: { restitution: 0.15, tangentKeep: 0.25, rollDecel: 10, damage: 0 },
}

/** Skipping a ball across the pond is a top-three delight moment. */
export const SKIM_MAX_DEG = 12
export const SKIM_MIN_SPEED = 22
export const SKIM_RESTITUTION = 0.5
export const SKIM_TANGENT_KEEP = 0.85

/** Steep landings in the rough plug dead. */
export const PLUG_VY = 18
/** Below this vertical speed after a bounce, the ball switches to rolling. */
export const ROLL_VY = 1.2
export const REST_SPEED = 0.15

// ── The range ────────────────────────────────────────────────────────────────

export const FAIRWAY_HALF_W = 14
export const ROUGH_HALF_W = 26
/** Long enough that the biggest drive in the game plus the golden-hour bonus
 *  still lands on grass rather than out of bounds. */
export const RANGE_LEN = 400
/** Left-side pond. */
export const WATER = { x0: -27, x1: -15, z0: 118, z1: 205 }
/** Right-side cart path, a narrow strip that rolls forever. */
export const CARTPATH = { x0: 15.5, x1: 19.5, z0: 55, z1: 320 }
export const BUNKER = { x: 12, z: 245, r: 7 }
/**
 * The green sits where an accurate golfer's ball is still rolling, and the cup
 * sits near its *back*, where that roll dies.
 *
 * Both facts were measured, not chosen. With the green centred on the cup at
 * 246m the ace was unreachable: Dana's best shot stopped 43m short, and moving
 * the green under her roll-out only meant its slick surface carried her past
 * the cup at speed. Putting the cup at the far end gives the ball the length of
 * the green to die on — which is what a hole-in-one actually is.
 */
export const GREEN = { x: 0, z: 210, r: 16 }
export const PIN = { x: 0, z: 215 }
export const CUP_R = 0.16

// ── Scoring ──────────────────────────────────────────────────────────────────

export const ZONE_MULT: Record<string, number> = {
	tee: 0.4,
	fairway: 1.0,
	green: 2.0,
	rough: 0.6,
	cartpath: 0.7,
	bunker: 0.5,
	water: 0,
	ob: 0.3,
}
export const HOLED_MULT = 10
export const HOLED_BONUS = 5000
export const PROXIMITY = [
	{ within: 3, bonus: 1000 },
	{ within: 10, bonus: 250 },
]
/**
 * Score multiplier per duck survived against the current golfer. The receipt
 * for the gamble: a monster drive struck at Volcanic after three ducks is worth
 * far more than the same drive struck calm, which is what makes standing there
 * while somebody gets angrier at you a decision rather than a dare.
 */
export const NERVE_MULT = 0.1
export const STYLE_FLUSH = 200
export const STYLE_SKIM = 300

// ── The ball's lifespan ──────────────────────────────────────────────────────

export const HP_MAX = 100
export const HP_PER_FLIGHT = 1
export const SCUFFED_AT = 60
export const CRACKED_AT = 30
/** Each damage event adds a permanent sidespin bias — the mechanic and the joke. */
export const SCUFF_BIAS_MIN = 0.05
export const SCUFF_BIAS_MAX = 0.15
/** Knuckleball wander below full health, m/s^2 at zero HP. */
export const FLUTTER_ACCEL = 5
export const FLUTTER_FREQ = 9
/** A cracked ball transfers less of the strike. */
export const CRACKED_SPEED_MULT = 0.92

// ── The day ──────────────────────────────────────────────────────────────────

export const SUN_NOTCHES = 24
export const SUN_COST = { dodge: 1, shot: 2, stormOff: 3, skip: 3 } as const

/**
 * The bucket. You choose a golfer by being on the mat when they step up, and
 * rolling back to wait for a better one costs daylight — which is the whole
 * budget: you can be greedy about contact quality or picky about golfers, not
 * both. Two skips cost the same as three shots.
 */
export const LOBBY_QUEUE = 3
/** How long to hold before the ball rolls back into the bucket. */
export const LOBBY_HOLD_TIME = 0.6
/** The last stretch of the day: golfers swing harder as the light goes. */
export const GOLDEN_HOUR_NOTCHES = 5
export const GOLDEN_POWER_BONUS = 0.08

// ── Fast-forward doomed flights ──────────────────────────────────────────────
/** Once past apex with nothing good left to happen, stop making the player watch. */
export const DOOMED_TIMESCALE = 2.5
